import { IconChevronDown, IconLogout, IconUserCircle } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import type { UserInfo } from "@/lib/types";
import { Avatar } from "../Avatar";
import { Badge } from "../Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu";

/**
 * Menú de usuario en el extremo derecho del header · avatar con iniciales
 * como trigger, dropdown con info de cuenta + badges de roles + logout.
 */
export function UserMenu({ user, onLogout }: { user: UserInfo | null; onLogout: () => void }) {
  const navigate = useNavigate();
  if (!user) return null;
  const iniciales = calcularIniciales(user.fullName || user.email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Menú de usuario"
          className="flex h-8 items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-1.5 text-muted-foreground transition-colors hover:bg-muted"
        >
          <Avatar
            size="sm"
            radius="xl"
            className="text-[11px] font-semibold ring-2 ring-multiva-200"
            style={{ backgroundColor: "var(--color-multiva-600)", color: "#fff" }}
          >
            {iniciales}
          </Avatar>
          <IconChevronDown size={12} strokeWidth={1.8} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-65">
        <div className="flex items-center gap-2.5 px-2 py-1.5">
          <Avatar size="md" radius="sm" color="multiva">
            {iniciales}
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{user.fullName || user.email}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="px-3 py-2">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Roles</p>
          <div className="flex flex-wrap gap-1">
            {user.roles.length === 0 ? (
              <span className="text-xs text-muted-foreground">Sin roles</span>
            ) : (
              user.roles.map((r) => (
                <Badge key={r} size="xs" variant="light" color="multiva">
                  {r}
                </Badge>
              ))
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        {/* La gestión de seguridad (2FA + contraseña) vive DENTRO de Mi
            perfil (pestaña Seguridad) · el item dedicado del menú se retiró
            a pedido del usuario, junto con la ruta /seguridad. */}
        <DropdownMenuItem onClick={() => void navigate({ to: "/perfil" })}>
          <IconUserCircle size={14} className="mr-1" /> Mi perfil
        </DropdownMenuItem>
        <DropdownMenuItem destructive onClick={onLogout}>
          <IconLogout size={14} className="mr-1" /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function calcularIniciales(input: string): string {
  return (
    input
      .split(/[\s@.]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "U"
  );
}
